import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart, type BottleSelection } from "@/context/cart-context";
import { useEvent } from "@/context/event-context";
import { formatPrice } from "@/lib/format";
import { Plus, Minus, Wine, Gift, DollarSign } from "lucide-react";
import type { TablePackage, BottleMenuItem } from "@/types/database";

/* ───────── helpers ───────── */

function getEffectiveUpcharge(bottle: BottleMenuItem, packagePrice: number): number {
  if (bottle.upcharge_waived_above > 0 && packagePrice >= bottle.upcharge_waived_above) {
    return 0;
  }
  return bottle.table_upcharge || 0;
}

interface ChargedBottle {
  bottleId: string;
  bottleName: string;
  quantity: number;
  /** per-unit charge for this bottle (0 = complimentary) */
  unitCharge: number;
  isComplimentary: boolean;
}

/**
 * Given selected bottles, figure out which are complimentary (cheapest first)
 * and which are extras charged at menu price.
 *
 * For included slots: charge = effective upcharge (may be 0 for waived tiers)
 * For extra slots: charge = full menu price
 */
function calculateCharges(
  selections: BottleSelection[],
  bottleMenu: BottleMenuItem[],
  includedCount: number,
  packagePrice: number,
): { charged: ChargedBottle[]; totalExtra: number; totalUpcharge: number } {
  // Flatten selections into individual units so we can sort per-unit
  const units: { bottleId: string; bottleName: string; price: number; upcharge: number }[] = [];
  for (const sel of selections) {
    const menuItem = bottleMenu.find((b) => b.id === sel.bottleId);
    const price = menuItem?.price ?? 0;
    const upcharge = getEffectiveUpcharge(menuItem!, packagePrice);
    for (let i = 0; i < sel.quantity; i++) {
      units.push({ bottleId: sel.bottleId, bottleName: sel.bottleName, price, upcharge });
    }
  }

  // Sort ascending by price so cheapest are complimentary first
  units.sort((a, b) => a.price - b.price);

  // First `includedCount` are complimentary (charge = upcharge only)
  // Rest are charged at full menu price
  const chargeMap = new Map<string, { comp: number; extra: number; upcharge: number; price: number }>();
  let totalExtra = 0;
  let totalUpcharge = 0;

  units.forEach((unit, idx) => {
    const key = unit.bottleId;
    if (!chargeMap.has(key)) {
      chargeMap.set(key, { comp: 0, extra: 0, upcharge: unit.upcharge, price: unit.price });
    }
    const entry = chargeMap.get(key)!;

    if (idx < includedCount) {
      // Complimentary slot — only upcharge applies
      entry.comp += 1;
      totalUpcharge += unit.upcharge;
    } else {
      // Extra — charged at menu price
      entry.extra += 1;
      totalExtra += unit.price;
    }
  });

  const charged: ChargedBottle[] = [];
  for (const [bottleId, entry] of chargeMap) {
    const sel = selections.find((s) => s.bottleId === bottleId)!;
    if (entry.comp > 0) {
      charged.push({
        bottleId,
        bottleName: sel.bottleName,
        quantity: entry.comp,
        unitCharge: entry.upcharge,
        isComplimentary: true,
      });
    }
    if (entry.extra > 0) {
      charged.push({
        bottleId,
        bottleName: sel.bottleName,
        quantity: entry.extra,
        unitCharge: entry.price,
        isComplimentary: false,
      });
    }
  }

  return { charged, totalExtra, totalUpcharge };
}

/* ───────── BottleRow ───────── */

function BottleRow({
  bottle,
  qty,
  onAdd,
  onRemove,
  packagePrice,
  basePrice,
  hasFreeSlots,
}: {
  bottle: BottleMenuItem;
  qty: number;
  onAdd: () => void;
  onRemove: () => void;
  packagePrice: number;
  basePrice: number;
  hasFreeSlots: boolean;
}) {
  let upgradeFee = 0;
  if (!hasFreeSlots || !bottle.is_base) {
    const adminOverride = getEffectiveUpcharge(bottle, packagePrice);
    if (adminOverride > 0 && hasFreeSlots) {
      upgradeFee = adminOverride;
    } else {
      upgradeFee = bottle.price;
    }
  }

  const showPrice = !hasFreeSlots || !bottle.is_base;
  const isSelected = qty > 0;

  return (
    <div
      className={`border transition-all overflow-hidden ${
        isSelected
          ? "border-ink bg-ink/[0.02]"
          : "border-ink/10 bg-white hover:border-ink/25 active:bg-ink/[0.02]"
      }`}
    >
      {/* Main row — tap anywhere to add the first one */}
      <button
        onClick={() => { if (!isSelected) onAdd(); }}
        className={`w-full flex items-center justify-between p-3.5 text-left transition-colors ${
          isSelected ? "cursor-default" : "cursor-pointer"
        }`}
      >
        <div className="min-w-0">
          <p className="font-sans text-sm font-medium text-ink">
            {bottle.brand}
          </p>
          <p className="font-serif-elegant text-xs italic text-ink/40">
            {bottle.size}{showPrice && ` — ${formatPrice(bottle.price)}`}
          </p>
        </div>
        {!isSelected ? (
          <span className="shrink-0 font-heading text-[10px] uppercase tracking-wider text-ink/30">
            Tap to add
          </span>
        ) : (
          <span className="shrink-0 font-heading text-sm font-extrabold text-ink">
            {qty}
          </span>
        )}
      </button>

      {/* Expanded controls — shows after first tap */}
      {isSelected && (
        <div className="flex items-center justify-between px-3.5 pb-3 pt-0">
          <button
            onClick={onRemove}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-ink/15 text-ink/60 hover:border-ink hover:text-ink transition-colors"
          >
            <Minus className="w-3 h-3" />
            <span className="font-heading text-[10px] uppercase tracking-wider">
              {qty === 1 ? "Remove" : "Less"}
            </span>
          </button>
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-ink text-white hover:bg-ink/80 transition-colors"
          >
            <Plus className="w-3 h-3" />
            <span className="font-heading text-[10px] uppercase tracking-wider">More</span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ───────── TableCustomizer ───────── */

interface TableCustomizerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tablePackage: TablePackage;
}

export function TableCustomizer({ open, onOpenChange, tablePackage }: TableCustomizerProps) {
  const { event } = useEvent();
  const { addItem } = useCart();
  const navigate = useNavigate();

  const [selectedBottles, setSelectedBottles] = useState<BottleSelection[]>([]);
  const [step, setStep] = useState<"bottles" | "review">("bottles");
  const [occasions, setOccasions] = useState<string[]>([]);
  const [specialNotes, setSpecialNotes] = useState("");
  // Track upgrades for fixed bottles: { originalBottleId: replacementBottleId }
  const [fixedUpgrades, setFixedUpgrades] = useState<Record<string, string>>({});
  const [upgradingBottleId, setUpgradingBottleId] = useState<string | null>(null);

  const bottles = (event?.bottle_menu || [])
    .filter((b: BottleMenuItem) => b.active)
    .sort((a: BottleMenuItem, b: BottleMenuItem) => a.sort_order - b.sort_order);

  // Find the cheapest complimentary bottle price (used for auto-calc upgrade fees)
  const baseBottles = bottles.filter((b: BottleMenuItem) => b.is_base);
  const basePrice = baseBottles.length > 0
    ? Math.min(...baseBottles.map((b: BottleMenuItem) => b.price))
    : 0;

  const totalBottlesSelected = selectedBottles.reduce((sum, b) => sum + b.quantity, 0);
  const choosableCount: number = tablePackage.choosable_complimentary_count || 0;
  const includedCount = choosableCount; // choosableCount is the source of truth for free slots
  const extraCount = Math.max(0, totalBottlesSelected - includedCount);

  // Calculate charges
  const { charged, totalExtra, totalUpcharge } = useMemo(
    () => calculateCharges(selectedBottles, bottles, includedCount, tablePackage.price),
    [selectedBottles, bottles, includedCount, tablePackage.price],
  );

  const totalExtraCharges = totalExtra + totalUpcharge;

  const addBottle = (bottle: BottleMenuItem) => {
    setSelectedBottles((prev) => {
      const existing = prev.find((b) => b.bottleId === bottle.id);
      if (existing) {
        return prev.map((b) =>
          b.bottleId === bottle.id ? { ...b, quantity: b.quantity + 1 } : b
        );
      }
      return [...prev, { bottleId: bottle.id, bottleName: bottle.brand, quantity: 1, upcharge: getEffectiveUpcharge(bottle, tablePackage.price) }];
    });
  };

  const removeBottle = (bottleId: string) => {
    setSelectedBottles((prev) => {
      const existing = prev.find((b) => b.bottleId === bottleId);
      if (existing && existing.quantity > 1) {
        return prev.map((b) =>
          b.bottleId === bottleId ? { ...b, quantity: b.quantity - 1 } : b
        );
      }
      return prev.filter((b) => b.bottleId !== bottleId);
    });
  };

  const handleAddToCart = () => {
    // Split into included bottles (on the table item) and extra bottles (separate cart items)
    const includedBottles: BottleSelection[] = [];
    const extraBottles: { bottleId: string; bottleName: string; quantity: number; menuPrice: number }[] = [];

    // Add all fixed complimentary bottles (with upgrade cost if swapped)
    for (const fb of allFixedBottles) {
      const upgradeId = fixedUpgrades[fb.id];
      const actualBottle = upgradeId
        ? bottles.find((b: BottleMenuItem) => b.id === upgradeId) || fb
        : fb;
      const upgradeCost = upgradeId && upgradeId !== fb.id
        ? Math.max(0, actualBottle.price - fb.price)
        : 0;
      includedBottles.push({
        bottleId: actualBottle.id,
        bottleName: actualBottle.brand,
        quantity: 1,
        upcharge: upgradeCost,
      });
    }

    for (const ch of charged) {
      if (ch.isComplimentary) {
        includedBottles.push({
          bottleId: ch.bottleId,
          bottleName: ch.bottleName,
          quantity: ch.quantity,
          upcharge: ch.unitCharge, // upcharge per bottle for included slots
        });
      } else {
        extraBottles.push({
          bottleId: ch.bottleId,
          bottleName: ch.bottleName,
          quantity: ch.quantity,
          menuPrice: ch.unitCharge, // full menu price
        });
      }
    }

    // Add table package with included bottles
    addItem({
      type: "table",
      itemId: tablePackage.id,
      name: tablePackage.name,
      quantity: 1,
      unitPrice: tablePackage.price,
      bottleSelections: includedBottles,
      mixerSelections: [],
      bottleCount: tablePackage.bottle_count,
      mixerLimit: 0,
      specialOccasion: occasions.length > 0 ? occasions : undefined,
      specialNotes: specialNotes.trim() || undefined,
    });

    // Add extra bottles as separate cart items
    for (const extra of extraBottles) {
      addItem({
        type: "bottle",
        itemId: extra.bottleId,
        name: extra.bottleName,
        quantity: extra.quantity,
        unitPrice: extra.menuPrice,
      });
    }

    setSelectedBottles([]);
    setOccasions([]);
    setSpecialNotes("");
    setStep("bottles");
    onOpenChange(false);
    navigate("/checkout");
  };

  const handleClose = () => {
    setSelectedBottles([]);
    setOccasions([]);
    setSpecialNotes("");
    setFixedUpgrades({});
    setUpgradingBottleId(null);
    setStep("bottles");
    onOpenChange(false);
  };

  // Separate base bottles from premium upgrades (baseBottles already defined above)
  const premiumBottles = bottles.filter((b: BottleMenuItem) => !b.is_base);

  // Group by spirit type
  const groupByType = (list: BottleMenuItem[]) => {
    const grouped: Record<string, BottleMenuItem[]> = {};
    list.forEach((b) => {
      if (!grouped[b.spirit_type]) grouped[b.spirit_type] = [];
      grouped[b.spirit_type].push(b);
    });
    return grouped;
  };

  const baseByType = groupByType(baseBottles);
  const premiumByType = groupByType(premiumBottles);

  // Fixed complimentary bottles (auto-included, no choice)
  const fixedBottleIds: string[] = tablePackage.fixed_bottle_ids || [];
  const fixedBottles = fixedBottleIds
    .map((id: string) => bottles.find((b: BottleMenuItem) => b.id === id))
    .filter(Boolean) as BottleMenuItem[];

  // How many bottles the customer must actively select
  // choosableCount is the source of truth — set in admin per package
  // If admin sets it to 0, no selection needed (fixed bottles only)
  const requiredSelections = choosableCount;

  const canProceed = requiredSelections === 0 || totalBottlesSelected >= requiredSelections;

  // Include complimentary_bottle_id as a fixed bottle (always, not just as fallback)
  const complimentaryBottle = tablePackage.complimentary_bottle_id
    ? bottles.find((b: BottleMenuItem) => b.id === tablePackage.complimentary_bottle_id)
    : null;

  // Merge: fixed_bottle_ids + complimentary_bottle_id (deduplicated)
  const allFixedBottles = [...fixedBottles];
  if (complimentaryBottle && !allFixedBottles.find((b) => b.id === complimentaryBottle.id)) {
    allFixedBottles.push(complimentaryBottle);
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="bg-cream border-ink/10 w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-lg uppercase tracking-wider text-ink">
            {tablePackage.name}
          </SheetTitle>
          <SheetDescription className="font-serif-elegant text-ink/60 italic">
            {formatPrice(tablePackage.price)} — {tablePackage.seats} guests
          </SheetDescription>
        </SheetHeader>

        {/* Step: Bottles */}
        {step === "bottles" && (
          <div className="mt-6 space-y-6">
            {/* ── Zone 1: Fixed Complimentary Bottles (with upgrade option) ── */}
            {allFixedBottles.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-emerald-600 shrink-0" />
                  <p className="font-heading text-xs uppercase tracking-wider text-emerald-700">
                    Included with your {tablePackage.name} package
                  </p>
                </div>
                <div className="space-y-2">
                  {allFixedBottles.map((originalBottle) => {
                    const upgradeId = fixedUpgrades[originalBottle.id];
                    const currentBottle = upgradeId
                      ? bottles.find((b: BottleMenuItem) => b.id === upgradeId) || originalBottle
                      : originalBottle;
                    const isUpgraded = !!upgradeId && upgradeId !== originalBottle.id;
                    const upgradeCost = isUpgraded ? Math.max(0, currentBottle.price - originalBottle.price) : 0;
                    const isShowingOptions = upgradingBottleId === originalBottle.id;

                    // Get bottles in the same spirit type for upgrade options
                    const upgradeOptions = bottles.filter(
                      (b: BottleMenuItem) => b.spirit_type === originalBottle.spirit_type && b.id !== originalBottle.id
                    );

                    return (
                      <div key={originalBottle.id}>
                        <div className="flex justify-between items-center bg-white/60 p-2 border border-emerald-100">
                          <div>
                            <p className="font-sans text-sm font-medium text-ink">
                              {currentBottle.brand}
                              {isUpgraded && (
                                <span className="ml-2 text-xs font-heading text-gold">
                                  +{formatPrice(upgradeCost)} upgrade
                                </span>
                              )}
                            </p>
                            <p className="font-serif-elegant text-xs italic text-emerald-600/70">{currentBottle.size}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {isUpgraded ? (
                              <button
                                onClick={() => {
                                  const next = { ...fixedUpgrades };
                                  delete next[originalBottle.id];
                                  setFixedUpgrades(next);
                                }}
                                className="font-heading text-[10px] uppercase tracking-wider text-ink/40 hover:text-ink transition-colors"
                              >
                                Reset
                              </button>
                            ) : null}
                            <span className="font-heading font-extrabold text-emerald-600 text-sm">
                              {isUpgraded ? formatPrice(upgradeCost) : "FREE"}
                            </span>
                          </div>
                        </div>
                        {upgradeOptions.length > 0 && (
                          <div className="mt-1">
                            {!isShowingOptions ? (
                              <button
                                onClick={() => setUpgradingBottleId(originalBottle.id)}
                                className="font-heading text-sm uppercase tracking-wider text-amber-500 font-bold hover:text-amber-400 transition-colors"
                              >
                                Upgrade {originalBottle.spirit_type} &rarr;
                              </button>
                            ) : (
                              <div className="bg-white border border-ink/10 p-2 mt-1 space-y-1">
                                <p className="font-heading text-[10px] uppercase tracking-wider text-ink/40 mb-1">
                                  Upgrade to:
                                </p>
                                {upgradeOptions.map((opt: BottleMenuItem) => {
                                  const diff = Math.max(0, opt.price - originalBottle.price);
                                  return (
                                    <button
                                      key={opt.id}
                                      onClick={() => {
                                        setFixedUpgrades({ ...fixedUpgrades, [originalBottle.id]: opt.id });
                                        setUpgradingBottleId(null);
                                      }}
                                      className="w-full flex justify-between items-center p-2 border border-ink/5 hover:border-gold hover:bg-gold/5 transition-colors"
                                    >
                                      <span className="font-sans text-sm text-ink">{opt.brand}</span>
                                      <span className="font-heading text-xs text-gold">
                                        {diff > 0 ? `+${formatPrice(diff)}` : "FREE"}
                                      </span>
                                    </button>
                                  );
                                })}
                                <button
                                  onClick={() => setUpgradingBottleId(null)}
                                  className="font-heading text-[10px] uppercase tracking-wider text-ink/30 hover:text-ink mt-1"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Zone 2: Browse & Add Bottles ── */}
            <div>
              <div className="flex justify-between items-start mb-1">
                <div>
                  <p className="font-heading text-sm uppercase tracking-wider text-ink flex items-center gap-2">
                    <Wine className="w-4 h-4" />
                    {choosableCount > 0
                      ? `Choose ${choosableCount} Complimentary Bottle${choosableCount !== 1 ? "s" : ""}`
                      : "Add Bottles"}
                  </p>
                  <p className="font-serif-elegant text-sm italic text-ink/50 mt-1">
                    {choosableCount > 0
                      ? `Pick ${choosableCount} from Standard at no charge, or upgrade to Premium`
                      : "Add bottles at menu price to enhance your table"}
                  </p>
                </div>
                {choosableCount > 0 && (
                  <Badge className="bg-ink text-white font-heading text-xs shrink-0">
                    {Math.min(totalBottlesSelected, requiredSelections)}/{requiredSelections} selected
                  </Badge>
                )}
              </div>

              {/* Completion message */}
              {choosableCount > 0 && totalBottlesSelected >= requiredSelections && (
                <div className="bg-emerald-50 border border-emerald-200 p-2 mt-3 mb-1">
                  <p className="font-heading text-xs uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                    ✓ All {requiredSelections} bottle{requiredSelections !== 1 ? "s" : ""} selected
                  </p>
                </div>
              )}

              {/* Note removed — headers now communicate this */}
            </div>

            {/* Base Bottles */}
            <p className="font-heading text-xs uppercase tracking-wider text-ink/60 border-b border-ink/10 pb-2">
              {choosableCount > 0 ? "Standard — No Charge" : "Standard"}
            </p>
            {Object.entries(baseByType).map(([type, typeBottles]) => (
              <div key={type}>
                <p className="font-heading text-xs uppercase tracking-wider text-ink/40 mb-3">
                  {type}
                </p>
                <div className="space-y-2">
                  {typeBottles.map((bottle: BottleMenuItem) => (
                    <BottleRow
                      key={bottle.id}
                      bottle={bottle}
                      qty={selectedBottles.find((b) => b.bottleId === bottle.id)?.quantity || 0}
                      onAdd={() => addBottle(bottle)}
                      onRemove={() => removeBottle(bottle.id)}
                      packagePrice={tablePackage.price}
                      basePrice={basePrice}
                      hasFreeSlots={choosableCount > 0}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Premium Upgrades */}
            {premiumBottles.length > 0 && (
              <>
                <div className="border-t border-gold/30 pt-4">
                  <p className="font-heading text-xs uppercase tracking-wider text-gold flex items-center gap-2 mb-1">
                    ★ Premium — Upgrade Fee Applies
                  </p>
                  <p className="font-serif-elegant text-xs italic text-ink/40 mb-4">
                    Upgrade charges apply per bottle when included; full menu price for extras
                  </p>
                </div>
                {Object.entries(premiumByType).map(([type, typeBottles]) => (
                  <div key={`premium-${type}`}>
                    <p className="font-heading text-xs uppercase tracking-wider text-ink/40 mb-3">
                      {type}
                    </p>
                    <div className="space-y-2">
                      {typeBottles.map((bottle: BottleMenuItem) => (
                        <BottleRow
                          key={bottle.id}
                          bottle={bottle}
                          qty={selectedBottles.find((b) => b.bottleId === bottle.id)?.quantity || 0}
                          onAdd={() => addBottle(bottle)}
                          onRemove={() => removeBottle(bottle.id)}
                          packagePrice={tablePackage.price}
                          basePrice={basePrice}
                          hasFreeSlots={false}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* ── Zone 3: Extra Bottles ── */}
            {extraCount > 0 && (
              <div className="bg-gold/10 border border-gold/20 p-3">
                <p className="font-heading text-xs uppercase tracking-wider text-ink flex items-center gap-2">
                  <DollarSign className="w-3 h-3" /> Want More? — Extra Bottles
                </p>
                <p className="font-serif-elegant text-xs italic text-ink/50 mt-1">
                  {extraCount} bottle{extraCount !== 1 ? "s" : ""} beyond your {includedCount} included —{" "}
                  charged at menu price ({formatPrice(totalExtra)})
                </p>
              </div>
            )}

            <Button
              onClick={() => setStep("review")}
              disabled={!canProceed}
              className="w-full bg-ink text-white hover:bg-gold hover:text-ink font-heading text-xs uppercase tracking-wider py-6"
            >
              {!canProceed
                ? `Select ${requiredSelections - totalBottlesSelected} more bottle${requiredSelections - totalBottlesSelected !== 1 ? "s" : ""}`
                : totalExtraCharges > 0
                  ? `Review Selection — +${formatPrice(totalExtraCharges)}`
                  : "Review Selection"}
            </Button>
          </div>
        )}

        {/* Step: Review */}
        {step === "review" && (
          <div className="mt-6 space-y-6">
            <p className="font-heading text-sm uppercase tracking-wider text-ink">
              Your Table Setup
            </p>

            {/* Package summary */}
            <div className="bg-white border border-ink/10 p-4">
              <p className="font-heading text-sm font-extrabold text-ink">{tablePackage.name}</p>
              <p className="font-serif-elegant text-sm italic text-ink/50">
                {tablePackage.seats} guests — {formatPrice(tablePackage.price)}
              </p>
            </div>

            {/* Fixed complimentary bottles in review (with upgrades) */}
            {allFixedBottles.length > 0 && (
              <div className="bg-white border border-ink/10 p-4">
                <p className="font-heading text-xs uppercase tracking-wider text-emerald-600/60 mb-2 flex items-center gap-1">
                  <Gift className="w-3 h-3" /> Comes with your package
                </p>
                <div className="space-y-1.5">
                  {allFixedBottles.map((originalBottle) => {
                    const upgradeId = fixedUpgrades[originalBottle.id];
                    const currentBottle = upgradeId
                      ? bottles.find((b: BottleMenuItem) => b.id === upgradeId) || originalBottle
                      : originalBottle;
                    const isUpgraded = !!upgradeId && upgradeId !== originalBottle.id;
                    const upgradeCost = isUpgraded ? Math.max(0, currentBottle.price - originalBottle.price) : 0;
                    return (
                      <div key={originalBottle.id} className="flex justify-between text-sm">
                        <span className="font-sans text-ink">
                          {currentBottle.brand} &times;1
                          {isUpgraded && (
                            <span className="ml-1 text-xs text-ink/40">(upgraded from {originalBottle.brand})</span>
                          )}
                        </span>
                        <span className={`font-heading font-extrabold ${isUpgraded ? "text-gold" : "text-emerald-600"}`}>
                          {isUpgraded ? `+${formatPrice(upgradeCost)}` : "FREE"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Bottles breakdown */}
            <div className="bg-white border border-ink/10 p-4">
              <p className="font-heading text-xs uppercase tracking-wider text-ink/40 mb-3">
                Bottles ({totalBottlesSelected}{allFixedBottles.length > 0 ? ` + ${allFixedBottles.length} complimentary` : ""})
              </p>
              <div className="space-y-2">
                {charged.map((ch, idx) => (
                  <div key={`${ch.bottleId}-${ch.isComplimentary}-${idx}`} className="flex justify-between text-sm">
                    <span className="font-sans text-ink flex items-center gap-1">
                      {ch.bottleName} &times;{ch.quantity}
                      {ch.isComplimentary ? (
                        <span className="inline-flex items-center gap-0.5 text-xs text-emerald-600 font-heading">
                          <Gift className="w-3 h-3" /> Your choice — Included
                          {ch.unitCharge > 0 && (
                            <span className="text-amber-600 ml-1">+{formatPrice(ch.unitCharge * ch.quantity)} upgrade</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-xs text-ink/50 font-heading">
                          Additional bottle — {formatPrice(ch.unitCharge)}/ea
                        </span>
                      )}
                    </span>
                    <span className="font-heading font-extrabold text-ink">
                      {ch.isComplimentary && ch.unitCharge === 0
                        ? "FREE"
                        : formatPrice(ch.unitCharge * ch.quantity)}
                    </span>
                  </div>
                ))}
              </div>
              {totalExtraCharges > 0 && (
                <div className="border-t border-ink/10 mt-3 pt-3 flex justify-between text-sm">
                  <span className="font-heading text-xs uppercase tracking-wider text-ink/60">
                    Additional bottle charges
                  </span>
                  <span className="font-heading font-extrabold text-ink">
                    +{formatPrice(totalExtraCharges)}
                  </span>
                </div>
              )}
            </div>

            {/* Special Occasion */}
            <div className="bg-white border border-ink/10 p-4">
              <p className="font-heading text-xs uppercase tracking-wider text-ink/40 mb-3">
                Celebrating something special?
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {["Birthday", "Anniversary", "Promotion", "Bachelor/Bachelorette", "Girls Night", "Just Because"].map((occasion) => {
                  const isSelected = occasions.includes(occasion);
                  return (
                    <button
                      key={occasion}
                      onClick={() =>
                        setOccasions((prev) =>
                          isSelected ? prev.filter((o) => o !== occasion) : [...prev, occasion]
                        )
                      }
                      className={`px-3 py-1.5 text-xs font-sans border transition-colors ${
                        isSelected
                          ? "bg-ink text-white border-ink"
                          : "bg-white text-ink/60 border-ink/15 hover:border-ink/30"
                      }`}
                    >
                      {occasion}
                    </button>
                  );
                })}
              </div>
              <textarea
                value={specialNotes}
                onChange={(e) => setSpecialNotes(e.target.value)}
                placeholder="Any special requests? (e.g., sparklers, cake, specific seating, dietary needs...)"
                className="w-full border border-ink/10 bg-cream/50 p-3 font-sans text-sm text-ink placeholder:text-ink/30 resize-none focus:outline-none focus:border-ink/30 transition-colors"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep("bottles")}
                className="flex-1 font-heading text-xs uppercase tracking-wider py-6"
              >
                Back
              </Button>
              <Button
                onClick={handleAddToCart}
                className="flex-1 bg-gold text-ink hover:bg-ink hover:text-white font-heading text-xs uppercase tracking-wider py-6"
              >
                Checkout — {formatPrice(tablePackage.price + totalExtraCharges)}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
